import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  mdiAlert,
  mdiAlertCircle,
  mdiArrowBottomRight,
  mdiArrowTopRight,
  mdiChartLine,
  mdiClose,
  mdiLightningBolt,
  mdiShieldCheck,
  mdiWifiOff,
} from "@mdi/js";
import MdiIcon from "../../../components/device/app/MdiIcon";
import AppModal from "../../../components/layout/app/AppModal";
import { useToggleTop } from "../../../components/common/ToggleTop";
import { UserService } from "../../../services/UserService";
import { User } from "../../../storage/store";
import { MODELS, isModel } from "../../../constants/devices";
import Lottie from "react-lottie";
import useMeterLive from "../../../hooks/useMeterLive";
import liveBlink from "../../../assets/json/live_blink.json";
import "../../../assets/styles/app-energy.css";

// Three-phase meters report each phase under its own suffix (_r, _y, _b, as
// the meter names them); the badge shows the phase letter in its wire colour.
const PHASES = [
  { label: "R", suffix: "r", color: "rgb(255, 85, 85)" },
  { label: "Y", suffix: "y", color: "#f4b003" },
  { label: "B", suffix: "b", color: "#1E88E5" },
];

// The app's blinking dot while live readings come in.
const LIVE_ANIMATION = { loop: true, autoplay: true, animationData: liveBlink, rendererSettings: { preserveAspectRatio: "xMidYMid meet" } };
const LiveDot = () => (
  <span className="hx-em-live" role="img" aria-label="Live readings">
    <Lottie options={LIVE_ANIMATION} width={33} height={33} isClickToPauseDisabled ariaRole="presentation" />
  </span>
);

// Each phase letter's tint behind it in the energy details cards.
const PHASE_TINTS = { R: "rgba(255, 85, 85, 0.12)", Y: "rgba(244, 176, 3, 0.15)", B: "rgba(30, 136, 229, 0.12)" };

const toNumber = (value) => Number(value ?? 0);
const fixed = (value) => toNumber(value).toFixed(2);

// '--' when the meter doesn't send a field (older firmware), rather than a misleading 0.
const detail = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : "--";
};

// R, Y and B values in a row, the phase letter in each card's corner.
const PhaseCards = ({ valueOf, unit, label }) => (
  <div className="hx-em-detail-phases" role="list" aria-label={label}>
    {PHASES.map(({ label: phase, suffix, color }) => (
      <div key={phase} className="hx-em-detail-card is-phase" role="listitem">
        <span className="hx-em-detail-value">{detail(valueOf(suffix))}</span>
        <span className="hx-em-detail-unit">
          <span className="hx-em-sr">Phase {phase}, </span>
          {unit}
        </span>
        <span className="hx-em-detail-letter" style={{ color, background: PHASE_TINTS[phase] }} aria-hidden="true">
          {phase}
        </span>
      </div>
    ))}
  </div>
);

// The meter's status code: 1 while it draws power, any other non-zero value
// while it feeds power back.
const flowText = (code) => {
  if (code === undefined || code === null || code === "" || Number(code) === 0) return "";
  return Number(code) === 1 ? "Consuming" : "Generating";
};

const NO_ALARMS = {
  underVoltageEnabled: false,
  underVoltageThreshold: 0,
  overVoltageEnabled: false,
  overVoltageThreshold: 0,
  overCurrentEnabled: false,
  overCurrentThreshold: 0,
  highTemperatureEnabled: false,
  highTemperatureThreshold: 0,
  leakageEnabled: false,
  leakageThreshold: 0,
  lostFlowEnabled: false,
  lostFlowThreshold: 0,
};

const alarmsFrom = (settings) => ({
  underVoltageEnabled: Number(settings.under_voltage_alarm_enable) === 1,
  underVoltageThreshold: Number(settings.under_voltage_threshold),
  overVoltageEnabled: Number(settings.over_voltage_alarm_enable) === 1,
  overVoltageThreshold: Number(settings.over_voltage_threshold),
  overCurrentEnabled: Number(settings.over_current_alarm_enable) === 1,
  overCurrentThreshold: Number(settings.over_current_threshold),
  highTemperatureEnabled: Number(settings.high_temperature_alarm_enable) === 1,
  highTemperatureThreshold: Number(settings.high_temperature_threshold),
  leakageEnabled: Number(settings.leakage_alarm_enable) === 1,
  leakageThreshold: Number(settings.leakage_threshold),
  lostFlowEnabled: Number(settings.lost_flow_alarm_enable) === 1,
  lostFlowThreshold: Number(settings.lost_flow_threshold),
});

// What the status card shows: the first alarm whose limit the readings cross,
// in red, or the voltage in green (the app's getEnergyStatus).
const statusOf = ({ voltage, current, temperature, leakage }, alarms, hasReadings) => {
  const currentLeft = { leftLabel: "Current", leftValue: current, leftUnit: "A" };
  const voltageLeft = { leftLabel: "Voltage", leftValue: voltage, leftUnit: "V" };
  const alarm = (label, icon, value, unit, left) => ({ alarm: true, label, icon, value, unit, ...left });
  const a = alarms;

  if (hasReadings) {
    if (a.underVoltageEnabled && voltage > 0 && voltage < a.underVoltageThreshold) {
      return alarm("Low Voltage", mdiAlert, voltage, "V", currentLeft);
    }
    if (a.overVoltageEnabled && voltage > 0 && voltage > a.overVoltageThreshold) {
      return alarm("Over Voltage", mdiAlertCircle, voltage, "V", currentLeft);
    }
    if (a.overCurrentEnabled && current > 0 && current > a.overCurrentThreshold) {
      return alarm("Over Current", mdiAlertCircle, current, "A", voltageLeft);
    }
    if (a.lostFlowEnabled && current > 0 && current < a.lostFlowThreshold) {
      return alarm("Lost Flow", mdiAlert, current, "A", voltageLeft);
    }
    if (a.highTemperatureEnabled && temperature > 0 && temperature > a.highTemperatureThreshold) {
      return alarm("High Temperature", mdiAlertCircle, temperature, "°C", currentLeft);
    }
    if (a.leakageEnabled && leakage > 0 && leakage > a.leakageThreshold) {
      return alarm("Leakage", mdiAlertCircle, leakage, "mA", currentLeft);
    }
  }
  return { alarm: false, label: "Voltage", icon: mdiShieldCheck, value: voltage, unit: "V", ...currentLeft };
};

// A label over its value. With onOpen the value opens that reading's graph.
const Reading = ({ label, value, onOpen }) => (
  <>
    <span className="hx-em-label">{label}</span>
    {onOpen ? (
      <button type="button" className="hx-em-value" onClick={onOpen} aria-label={`${label} ${value}, open graph`}>
        {value}
      </button>
    ) : (
      <span className="hx-em-value">{value}</span>
    )}
  </>
);

// A three-phase meter's energy details (the app's Energy details modal): active
// energy and reactive power by phase, reactive energy, and a way to the graph.
// details.data is the meter's latest telemetry; live has the readings shown on
// the page, which carry the reactive power.
const EnergyDetails = ({ details, live, onClose, onGraph }) => {
  const data = details?.data ?? {};
  const updated = data.last_updated ? moment(data.last_updated) : null;
  const subtitle = `3-phase meter${updated?.isValid() ? ` · updated ${updated.format("DD MMM, hh:mm A")}` : ""}`;

  return (
    <AppModal open={Boolean(details)} onClose={onClose} title="Energy details" className="hx-em-detail">
      <div className="hx-em-detail-head">
        <span className="hx-em-detail-icon" aria-hidden="true">
          <MdiIcon path={mdiLightningBolt} size={22} color="rgb(71, 177, 116)" />
        </span>
        <div className="hx-em-detail-heading">
          <h2>Energy details</h2>
          <p>{subtitle}</p>
        </div>
        {details?.loading ? <span className="hx-em-detail-spin" role="status" aria-label="Refreshing" /> : null}
        <button type="button" className="hx-em-detail-close" onClick={onClose} aria-label="Close">
          <MdiIcon path={mdiClose} size={22} color="currentColor" />
        </button>
      </div>

      <h3 className="hx-em-detail-title">Total combined active energy by phase</h3>
      <PhaseCards label="Active energy by phase" unit="kWh" valueOf={(suffix) => data[`energy_${suffix}`]} />

      <h3 className="hx-em-detail-title">Reactive power by phase</h3>
      <PhaseCards
        label="Reactive power by phase"
        unit="kvar"
        valueOf={(suffix) => live[`reactive_power_${suffix}`] ?? data[`reactive_power_${suffix}`]}
      />

      <h3 className="hx-em-detail-title">Reactive energy</h3>
      <div className="hx-em-detail-pair">
        {[
          { label: "Consumed", icon: mdiArrowBottomRight, value: data.fwd_reactive_energy },
          { label: "Generated", icon: mdiArrowTopRight, value: data.rev_reactive_energy },
        ].map((item) => (
          <div key={item.label} className="hx-em-detail-card">
            <span className="hx-em-detail-label">
              <MdiIcon path={item.icon} size={14} color="currentColor" />
              {item.label}
            </span>
            <span className="hx-em-detail-value">
              {detail(item.value)} <span className="hx-em-detail-unit">kvarh</span>
            </span>
          </div>
        ))}
      </div>
      <div className="hx-em-detail-total">
        <span>Total reactive energy</span>
        <b>{detail(data.total_reactive_energy)} kvarh</b>
      </div>

      <button type="button" className="hx-em-detail-graph" onClick={onGraph}>
        <MdiIcon path={mdiChartLine} size={18} color="#fff" />
        View history graph
      </button>
    </AppModal>
  );
};

// An energy meter's readings on its detail screen, as the app draws them: the
// status card (one per phase on three-phase meters), energy consumed and
// generated, and the other readings in a row.
const EnergyMeterPanel = ({ device }) => {
  const navigate = useHistory();
  const toast = useToggleTop();
  const deviceId = device.deviceId;
  const [reading, setReading] = useState(null);
  const { live, isLive } = useMeterLive(device);
  const [meterStatus, setMeterStatus] = useState(null);
  const [alarms, setAlarms] = useState(NO_ALARMS);
  const [loaded, setLoaded] = useState(false);
  const [offline, setOffline] = useState(false);
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [details, setDetails] = useState(null);
  const phasesRef = useRef(null);

  useEffect(() => {
    let current = true;
    (async () => {
      const [telemetry, last, record] = await Promise.allSettled([
        UserService.getEnergyTelemetry(deviceId),
        UserService.getLastStatebydeviceId(User.getPropertyId(), deviceId),
        UserService.getDevice(deviceId),
      ]);
      if (!current) return;
      if (telemetry.status === "fulfilled") {
        if (telemetry.value) setReading(telemetry.value);
      } else {
        toast("Couldn't load the meter's readings. Please try again.", "red");
      }
      if (last.status === "fulfilled" && last.value) {
        setMeterStatus(last.value.devices?.[deviceId] ?? null);
        setOffline(last.value.isOnline === false);
        setOfflineOpen(last.value.isOnline === false);
      }
      const settings = record.status === "fulfilled" ? record.value?.[0]?.device?.deviceSettings?.[0]?.settings : null;
      if (settings) setAlarms(alarmsFrom(settings));
      setLoaded(true);
    })();
    return () => {
      current = false;
    };
    // toast is a new function on every render; loading once per meter is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const openGraph = (metric) => navigate.push("/energy-graph", { data: device, metric });

  // The saved readings, with whatever has come in live since on top.
  const r = { ...(reading ?? {}), ...(live ?? {}) };
  const hasReadings = reading !== null || live !== null;
  const singlePhase = isModel(device, MODELS.W1EMP_1);
  const threePhase = isModel(device, MODELS.W3EMP_1);

  // On a three-phase meter, Energy Consumed and Generated open the energy
  // details, read fresh from the meter; the graph is a button inside.
  const openDetails = async (metric) => {
    setDetails({ metric, loading: true, data: reading });
    try {
      const telemetry = await UserService.getEnergyTelemetry(deviceId);
      if (telemetry) {
        setDetails((prev) => prev && { ...prev, data: telemetry });
        setReading((prev) => ({ ...prev, ...telemetry }));
      }
    } catch (error) {
      toast("Couldn't refresh the meter's readings. Showing the last ones.", "red");
    } finally {
      setDetails((prev) => prev && { ...prev, loading: false });
    }
  };

  const openEnergy = (metric) => (threePhase ? openDetails(metric) : openGraph(metric));
  const temperature = toNumber(r.temp);
  const leakage = toNumber(r.leakage_c);
  // On each status card: the live dot, and the grey cover while it's offline.
  const cardExtras = (
    <>
      {isLive ? <LiveDot /> : null}
      {offline ? <span className="hx-em-offline" aria-hidden="true" /> : null}
    </>
  );

  const showPhase = (index) => {
    const list = phasesRef.current;
    if (list) list.scrollTo({ left: index * list.clientWidth, behavior: "smooth" });
  };

  const onPhaseScroll = () => {
    const list = phasesRef.current;
    if (list && list.clientWidth > 0) setPhaseIndex(Math.round(list.scrollLeft / list.clientWidth));
  };

  let statusCard = null;
  if (threePhase) {
    statusCard = (
      <>
        <div className="hx-em-phases" ref={phasesRef} onScroll={onPhaseScroll}>
          {PHASES.map(({ label, suffix, color }) => {
            const voltage = toNumber(r[`voltage_${suffix}`]);
            const current = toNumber(r[`current_${suffix}`]);
            const status = statusOf({ voltage, current, temperature, leakage }, alarms, hasReadings);
            return (
              <div key={label} className="hx-em-phase" role="group" aria-label={`Phase ${label}`}>
                <div className={`hx-em-status is-phase ${status.alarm ? "is-alarm" : ""}`}>
                  <div className="hx-em-col">
                    <Reading label={status.leftLabel} value={`${fixed(status.leftValue)} ${status.leftUnit}`} />
                    <Reading
                      label="Power Factor"
                      value={`${toNumber(r[`pf_${suffix}`])} Pf`}
                      onOpen={() => openGraph("power_factor")}
                    />
                  </div>
                  <span className="hx-em-divider" />
                  <div className="hx-em-col is-middle">
                    {status.icon ? <MdiIcon path={status.icon} size={18} color="#fff" style={{ marginBottom: 2 }} /> : null}
                    <Reading label={status.label} value={`${fixed(status.value)} ${status.unit}`} />
                    <span className="hx-em-flow">{flowText(meterStatus?.[`status_${suffix}`])}</span>
                  </div>
                  <span className="hx-em-divider" />
                  <div className="hx-em-col">
                    <Reading label="Power" value={`${fixed(r[`active_power_${suffix}`])} kW`} />
                    <Reading label="Frequency" value={`${toNumber(r[`freq_${suffix}`])} Hz`} />
                  </div>
                  <span className="hx-em-badge" style={{ color }} aria-hidden="true">
                    {label}
                  </span>
                  {cardExtras}
                </div>
              </div>
            );
          })}
        </div>
        <div className="hx-em-dots">
          {PHASES.map(({ label }, index) => (
            <button
              key={label}
              type="button"
              className={`hx-em-dot ${index === phaseIndex ? "is-active" : ""}`}
              aria-label={`Show phase ${label}`}
              aria-current={index === phaseIndex}
              onClick={() => showPhase(index)}
            />
          ))}
        </div>
      </>
    );
  } else if (singlePhase) {
    const voltage = toNumber(r.voltage ?? r.voltage_a);
    const current = toNumber(r.current ?? r.current_a);
    const status = statusOf({ voltage, current, temperature, leakage }, alarms, hasReadings);
    statusCard = (
      <div className={`hx-em-status ${status.alarm ? "is-alarm" : ""}`}>
        <div className="hx-em-col">
          <Reading
            label={status.leftLabel}
            value={`${fixed(status.leftValue)} ${status.leftUnit}`}
            onOpen={status.leftLabel === "Current" ? () => openGraph("current") : undefined}
          />
        </div>
        <span className="hx-em-divider" />
        <div className="hx-em-col is-middle">
          <MdiIcon path={status.icon} size={26} color="#fff" />
          <Reading
            label={status.label}
            value={`${fixed(status.value)} ${status.unit}`}
            onOpen={status.label === "Voltage" ? () => openGraph("voltage") : undefined}
          />
          <span className="hx-em-flow">{flowText(meterStatus?.status)}</span>
        </div>
        <span className="hx-em-divider" />
        <div className="hx-em-col">
          <Reading label="Power" value={`${fixed(r.active_power ?? r.active_power_a)} kW`} onOpen={() => openGraph("power")} />
        </div>
        {cardExtras}
      </div>
    );
  }

  // A reading with a metric opens its graph: Max Demand its highest over time,
  // Power Factor its average.
  const stats = [
    { label: "Total Energy", value: `${r.total_energy ?? 0} KWh` },
    { label: "Max Demand", value: `${r.max_demand ?? 0} kVA`, metric: "max_demand" },
    { label: "Power Factor", value: `${r.pf ?? r.pf_a ?? r.power_factor ?? 0} Pf`, metric: "power_factor" },
    { label: "Reactive Power", value: `${r.reactive_power ?? 0} kvar` },
    { label: "Leakage Current", value: `${r.leakage_c ?? 0} mA` },
    { label: "Temperature", value: `${r.temp ?? 0} °C` },
    ...(threePhase ? [{ label: "None Sequence Current", value: `${r.n_current ?? 0} A` }] : []),
  ];

  return (
    <div className="hx-em">
      {!loaded ? <div className="hx-skel hx-em-skel" aria-label="Loading readings" /> : statusCard}

      <div className="hx-em-mini-row">
        <button type="button" className="hx-em-mini" onClick={() => openEnergy("consumed")}>
          <span className="hx-em-mini-label">Energy Consumed</span>
          <span className="hx-em-mini-value">{fixed(r.fwd_energy)} KWh</span>
        </button>
        <button type="button" className="hx-em-mini" onClick={() => openEnergy("generated")}>
          <span className="hx-em-mini-label">Energy Generated</span>
          <span className="hx-em-mini-value">{fixed(r.rev_energy)} KWh</span>
        </button>
      </div>

      <div className="hx-em-stats">
        {stats.map((stat) =>
          stat.metric ? (
            <button key={stat.label} type="button" className="hx-em-stat is-graph" onClick={() => openGraph(stat.metric)}>
              <span className="hx-em-stat-value">{stat.value}</span>
              <span className="hx-em-stat-label">{stat.label}</span>
            </button>
          ) : (
            <div key={stat.label} className="hx-em-stat">
              <span className="hx-em-stat-value">{stat.value}</span>
              <span className="hx-em-stat-label">{stat.label}</span>
            </div>
          )
        )}
      </div>

      {threePhase ? (
        <EnergyDetails
          details={details}
          live={r}
          onClose={() => setDetails(null)}
          onGraph={() => {
            const metric = details?.metric;
            setDetails(null);
            openGraph(metric);
          }}
        />
      ) : null}

      <AppModal
        open={offlineOpen}
        onClose={() => setOfflineOpen(false)}
        title="Device Offline"
        className="hx-em-offline-dialog"
      >
        <span className="hx-em-offline-icon" aria-hidden="true">
          <MdiIcon path={mdiWifiOff} size={27} color="#868E96" />
        </span>
        <p className="hx-em-offline-text">
          <b>{device.name}</b> is currently offline. Please check the device connection.
        </p>
        <button type="button" className="hx-em-offline-ok" onClick={() => setOfflineOpen(false)}>
          OK
        </button>
      </AppModal>
    </div>
  );
};

export default EnergyMeterPanel;
